// RetoRealtimePage.js — Reto Semanal por OpenAI Realtime
// Flujo: el modelo lee la pregunta, el alumno responde por voz, el backend
// evalúa con IA y devuelve retroalimentación. UI inspirada en EvaluacionRealtimePage.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useIdioma } from '../contexts/IdiomaContext';
import { retoRealtimeService, retoService } from '../services/api';
import RealtimeService, { SESSION_STATES } from '../services/realtimeService';

// ═════ animations ═════
const slideUp   = keyframes`from { opacity:0; transform: translateY(20px); } to { opacity:1; transform: translateY(0); }`;
const fadeIn    = keyframes`from { opacity:0; } to { opacity:1; }`;
const pulse     = keyframes`0%,100% { transform: scale(1); opacity:.7; } 50% { transform: scale(1.18); opacity:1; }`;
const ringPulse = keyframes`0% { box-shadow: 0 0 0 0 rgba(239,68,68,.55); } 100% { box-shadow: 0 0 0 22px rgba(239,68,68,0); }`;
const wave      = keyframes`0%,100% { transform: scaleY(0.4); } 50% { transform: scaleY(1.2); }`;
const countup   = keyframes`from { transform: scale(0.6); opacity:0; } 60% { transform: scale(1.08); opacity:1; } to { transform: scale(1); }`;

// ═════ layout ═════
const Page = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at 20% 10%, rgba(251,191,36,0.22) 0%, transparent 50%),
    radial-gradient(circle at 80% 90%, rgba(245,158,11,0.18) 0%, transparent 50%),
    linear-gradient(160deg, #0e1117 0%, #1c1410 100%);
  color: #e2e8f0;
  display: flex; flex-direction: column;
  padding: clamp(0.75rem, 2vw, 1.5rem);
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
`;
const Container = styled.div`
  width: 100%; max-width: 720px; margin: 0 auto; flex: 1;
  display: flex; flex-direction: column; gap: 0.9rem;
`;
const TopBar = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 0.25rem 0.1rem;
`;
const DocTitle = styled.div`
  font-size: 0.82rem; opacity: 0.75; font-weight: 600; letter-spacing: 0.3px;
  display: flex; align-items: center; gap: 0.5rem;
`;
const IdiomaPill = styled.span`
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.12);
  padding: 2px 9px; border-radius: 999px; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.5px;
`;

// ─ Question card ─
const QuestionCard = styled.div`
  background: linear-gradient(160deg, rgba(255,255,255,0.075) 0%, rgba(255,255,255,0.035) 100%);
  border: 1px solid rgba(251, 191, 36, 0.25);
  border-radius: 22px;
  padding: clamp(1.5rem, 3.5vw, 2.25rem) clamp(1.25rem, 3.5vw, 2rem);
  backdrop-filter: blur(20px) saturate(1.3);
  -webkit-backdrop-filter: blur(20px) saturate(1.3);
  box-shadow:
    0 12px 40px rgba(0,0,0,0.35),
    inset 0 1px 0 rgba(255,255,255,0.08),
    inset 0 0 60px rgba(251, 191, 36, 0.05);
  animation: ${slideUp} 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative; overflow: hidden;
`;
const Pill = styled.span`
  display: inline-flex; align-items: center; gap: 6px;
  background: linear-gradient(135deg, rgba(251,191,36,0.18), rgba(245,158,11,0.10));
  color: #FBBF24;
  border: 1px solid rgba(251,191,36,0.35);
  padding: 5px 12px; border-radius: 999px;
  font-size: 0.7rem; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase;
  margin-bottom: 1.25rem;
`;
const Pregunta = styled.div`
  font-size: clamp(1.15rem, 3vw, 1.55rem);
  line-height: 1.55; color: #f8fafc; font-weight: 400;
  min-height: 80px;
  animation: ${fadeIn} 0.4s ease;
`;
const Cursor = styled.span`
  display: inline-block; width: 2px; height: 1.1em; background: #FBBF24;
  margin-left: 4px; vertical-align: text-bottom;
  animation: ${pulse} 0.9s ease infinite;
`;

// ─ Visualizer ─
const Waves = styled.div`
  display: flex; align-items: center; justify-content: center;
  gap: 4px; height: 28px; margin-top: 0.75rem;
  opacity: ${p => p.$active ? 1 : 0.25}; transition: opacity 0.3s;
`;
const Bar = styled.div`
  width: 3px; height: 100%;
  background: linear-gradient(180deg, #FBBF24, #B45309);
  border-radius: 2px; transform-origin: center;
  ${p => p.$active && css`animation: ${wave} ${0.6 + p.$i * 0.08}s ease infinite;`}
`;

// ─ Input zone ─
const InputCard = styled.div`
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px; padding: 1rem; backdrop-filter: blur(12px);
  display: flex; flex-direction: column; gap: 0.6rem;
`;
const Textarea = styled.textarea`
  width: 100%; box-sizing: border-box;
  background: rgba(0,0,0,0.25); color: #fff;
  border: 1px solid rgba(255,255,255,0.1); border-radius: 12px;
  padding: 0.85rem 1rem; font: inherit; font-size: 0.95rem; line-height: 1.4;
  resize: none; min-height: 56px; max-height: 160px;
  &:focus { outline: none; border-color: #FBBF24; box-shadow: 0 0 0 3px rgba(251,191,36,0.15); }
  &::placeholder { color: rgba(255,255,255,0.35); }
  &:disabled { opacity: 0.5; }
`;
const ActionsRow = styled.div`
  display: flex; gap: 0.5rem; align-items: center;
`;
const MicBtn = styled.button`
  position: relative;
  width: 56px; height: 56px; flex-shrink: 0;
  border-radius: 50%; border: none; cursor: pointer;
  background: ${p => p.$listening ? 'linear-gradient(135deg, #ef4444, #b91c1c)' :
                     p.$speaking  ? 'linear-gradient(135deg, #FBBF24, #B45309)' :
                     'rgba(255,255,255,0.08)'};
  color: #fff; font-size: 1.5rem;
  display: flex; align-items: center; justify-content: center;
  ${p => p.$listening && css`animation: ${ringPulse} 1.4s ease infinite;`}
  &:disabled { opacity: 0.4; cursor: not-allowed; animation: none; }
`;
const StatusLabel = styled.div`
  flex: 1; min-width: 0; font-size: 0.85rem; opacity: 0.75;
  display: flex; align-items: center; gap: 0.4rem;
`;
const StatusDot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${p => p.$color || '#94a3b8'};
  box-shadow: 0 0 8px ${p => p.$color || '#94a3b8'};
`;
const SendBtn = styled.button`
  background: linear-gradient(135deg, #FBBF24, #B45309);
  color: #fff; border: none; border-radius: 12px;
  padding: 0.75rem 1.25rem; font-weight: 700; font-size: 0.9rem; cursor: pointer;
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

// ─ Result ─
const ResultCard = styled.div`
  background: rgba(255,255,255,0.06); border: 1px solid rgba(251,191,36,0.3);
  border-radius: 24px; padding: 2.25rem 1.5rem; text-align: center;
  backdrop-filter: blur(20px); box-shadow: 0 20px 60px rgba(0,0,0,0.4);
  animation: ${slideUp} 0.5s ease;
`;
const Score = styled.div`
  font-size: clamp(3.5rem, 12vw, 5.5rem); font-weight: 900;
  background: ${p => p.$ok ? 'linear-gradient(135deg, #34d399, #10b981)' :
                     'linear-gradient(135deg, #fca5a5, #ef4444)'};
  -webkit-background-clip: text; background-clip: text; color: transparent;
  line-height: 1; margin: 0.5rem 0;
  animation: ${countup} 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
`;
const ResultBadge = styled.div`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 0.5rem 1.1rem; border-radius: 999px;
  font-size: 0.85rem; font-weight: 800; letter-spacing: 0.5px;
  background: ${p => p.$ok ? 'rgba(16,185,129,0.18)' : 'rgba(239,68,68,0.18)'};
  color: ${p => p.$ok ? '#34d399' : '#fca5a5'};
  border: 1px solid ${p => p.$ok ? 'rgba(16,185,129,0.45)' : 'rgba(239,68,68,0.45)'};
`;
const RetroBlock = styled.div`
  margin-top: 1.25rem; padding: 1rem 1.2rem;
  background: linear-gradient(135deg, rgba(251,191,36,0.10), rgba(245,158,11,0.05));
  border: 1px solid rgba(251,191,36,0.3); border-radius: 14px;
  text-align: left;
`;
const Btn = styled.button`
  background: ${p => p.$primary ? 'linear-gradient(135deg, #FBBF24, #D97706)' : 'rgba(255,255,255,0.08)'};
  color: #fff; border: 1px solid ${p => p.$primary ? 'transparent' : 'rgba(255,255,255,0.15)'};
  border-radius: 12px; padding: 0.75rem 1.25rem; font-weight: 700; font-size: 0.9rem;
  cursor: pointer;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

// ═══════════ main ═══════════
const RetoRealtimePage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, idioma } = useIdioma();

  // pre | sin_reto | conectando | en_curso | evaluando | resultado | error
  const [stage, setStage] = useState('pre');
  const [meta, setMeta] = useState(null);
  const [pregunta, setPregunta] = useState('');
  const [streamingAi, setStreamingAi] = useState('');
  const [inputText, setInputText] = useState('');
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [aiState, setAiState] = useState(SESSION_STATES?.DISCONNECTED || 'disconnected');
  const [thinking, setThinking] = useState(false);
  const [sinRetoInfo, setSinRetoInfo] = useState(null);
  const [micUnavailable, setMicUnavailable] = useState(false);
  const [noVoiceDetected, setNoVoiceDetected] = useState(false);

  const clientRef = useRef(null);
  const streamingBufferRef = useRef('');
  const streamingTickRef = useRef(null);
  const retoIdRef = useRef(null);
  // Acumulamos lo que el alumno DIJO realmente (transcripción Whisper) o escribió.
  // Usado para detectar cuando el modelo intenta submit sin que el alumno haya hablado.
  const userInputsRef = useRef([]);

  const scheduleStreamingFlush = useCallback(() => {
    if (streamingTickRef.current) return;
    streamingTickRef.current = requestAnimationFrame(() => {
      streamingTickRef.current = null;
      setStreamingAi(streamingBufferRef.current);
    });
  }, []);

  // Llama al backend para evaluar la respuesta y le devuelve la retro al modelo.
  const evaluarYDevolver = useCallback(async (respuestaUsuario) => {
    try {
      const res = await retoService.responderReto(retoIdRef.current, user.id, respuestaUsuario);
      const data = res.data || {};
      const evalData = {
        es_correcta: !!data.es_correcta,
        puntuacion: Number(data.puntuacion || 0),
        retroalimentacion: data.retroalimentacion || '',
      };
      setResultado(evalData);
      return evalData;
    } catch (e) {
      const msg = e?.response?.data?.error || e?.message || 'Error al evaluar';
      setError(msg);
      return { es_correcta: false, puntuacion: 0, retroalimentacion: msg };
    }
  }, [user?.id]);

  const handleFunctionCall = useCallback(async (name, args) => {
    if (name === 'submit_respuesta_reto') {
      const respuesta = String(args.respuesta_usuario || '').trim();
      const userTotalChars = userInputsRef.current.join(' ').trim().length;

      // Defensa 1: el alumno NUNCA habló ni escribió → el modelo inventó la respuesta.
      // (caso real: alumno en silencio, modelo dijo "Amo mi diseño..." y la persistió)
      if (userTotalChars === 0) {
        setNoVoiceDetected(true);
        return {
          output: JSON.stringify({
            error: 'no_voice_detected',
            message: 'El sistema NO capturó ninguna respuesta del alumno. PROHIBIDO inventar texto. Decile cortésmente: "No escuché tu respuesta, ¿podés repetirla más fuerte o escribirla?" y espera de nuevo en silencio.',
          }),
          triggerResponse: true,
        };
      }
      // Defensa 2: respuesta vacía o demasiado corta para evaluar.
      if (respuesta.length < 5) {
        return {
          output: JSON.stringify({
            error: 'no_response_yet',
            message: 'El alumno aún no respondió o la respuesta es ininteligible. NO llames submit otra vez hasta escuchar una respuesta clara y completa.',
          }),
          triggerResponse: false,
        };
      }
      setNoVoiceDetected(false);
      setThinking(true);
      const evalData = await evaluarYDevolver(respuesta);
      setThinking(false);
      // Devolvemos al modelo la evaluación oficial para que la diga por voz.
      return { output: JSON.stringify(evalData), triggerResponse: true };
    }
    if (name === 'finalizar_reto') {
      // Si por algún motivo no llegó submit antes, igual nos quedamos con los args
      if (!resultado) {
        setResultado({
          es_correcta: !!args.es_correcta,
          puntuacion: Number(args.puntuacion || 0),
          retroalimentacion: args.retroalimentacion || '',
        });
      }
      setStage('resultado');
      clientRef.current?.disconnect();
      return { output: 'finalized', triggerResponse: false };
    }
    return null;
  }, [evaluarYDevolver, resultado]);

  const startSession = useCallback(async () => {
    setError(null);
    setStage('conectando');

    // Pre-fetch para saber si hay reto disponible (y obtener reto_id antes de Realtime)
    try {
      const probe = await retoRealtimeService.iniciar(parseInt(documentId, 10));
      const data = probe.data || {};
      if (!data.tiene_reto) {
        setSinRetoInfo({
          mensaje: data.mensaje || 'Sin reto disponible',
          proximo: data.proximo_reto || null,
          completado: !!data.reto_completado,
        });
        setStage('sin_reto');
        return;
      }
      retoIdRef.current = data.reto?.id || null;
      setPregunta(data.reto?.pregunta || '');
      setMeta({ document_title: data.document_title, voice: data.voice, model: data.model });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Error iniciando reto');
      setStage('error');
      return;
    }

    // Conectamos Realtime usando el mismo endpoint (re-fetch en el service del Realtime).
    // El backend genera un client_secret nuevo cada vez — ok.
    const client = new RealtimeService({
      documentId: parseInt(documentId, 10),
      mode: 'reto',
      sessionEndpoint: '/reto-realtime/iniciar.php',
      autoGreeting: false,
      autoStart: true, // que arranque leyendo la pregunta inmediatamente al conectar
      onAITranscript: (text, isDone) => {
        if (!isDone) {
          streamingBufferRef.current = (streamingBufferRef.current || '') + (text || '');
          scheduleStreamingFlush();
        } else {
          if (streamingTickRef.current) { cancelAnimationFrame(streamingTickRef.current); streamingTickRef.current = null; }
          streamingBufferRef.current = ''; setStreamingAi('');
        }
      },
      onStateChange: (s) => setAiState(s),
      onFunctionCall: handleFunctionCall,
      onThinking: (on) => setThinking(!!on),
      onMicError: () => { setMicUnavailable(true); },
      onTranscript: (text, isDone) => {
        // Whisper devolvió lo que dijo realmente el alumno → lo guardamos para
        // poder detectar después si el modelo inventó una respuesta.
        if (isDone !== false && text && text.trim()) {
          userInputsRef.current.push(text.trim());
          setNoVoiceDetected(false);
        }
      },
      onError: (e) => { setError(e || 'Error de conexión'); setStage('error'); },
    });
    clientRef.current = client;
    setStage('en_curso');
    await client.connect();
  }, [documentId, handleFunctionCall, scheduleStreamingFlush]);

  const sendText = () => {
    const txt = inputText.trim();
    if (!txt) return;
    // El texto escrito también cuenta como input real del alumno.
    userInputsRef.current.push(txt);
    setNoVoiceDetected(false);
    clientRef.current?.sendUserText(txt);
    setInputText('');
  };

  useEffect(() => () => { clientRef.current?.disconnect(); }, []);

  const statusInfo = () => {
    if (stage === 'conectando') return { dot: '#f59e0b', txt: 'Conectando…' };
    if (thinking) {
      const txt = idioma === 'en' ? 'Evaluating your answer…' : idioma === 'pt' ? 'Avaliando sua resposta…' : 'Evaluando tu respuesta…';
      return { dot: '#FBBF24', txt };
    }
    if (aiState === SESSION_STATES.AI_SPEAKING) return { dot: '#FBBF24', txt: 'El mentor habla' };
    if (aiState === SESSION_STATES.LISTENING) return { dot: '#ef4444', txt: 'Te escucho' };
    return { dot: '#10b981', txt: 'Tu turno' };
  };

  // ═════ PRE ═════
  if (stage === 'pre') {
    return (
      <Page>
        <Container>
          <QuestionCard style={{ textAlign: 'center', paddingTop: '3rem', paddingBottom: '2.5rem' }}>
            <div style={{ fontSize: '3.5rem', marginBottom: '0.5rem' }}>🎯</div>
            <h1 style={{ margin: '0 0 0.75rem', fontSize: 'clamp(1.5rem, 4vw, 2rem)' }}>Reto Semanal</h1>
            <p style={{ color: 'rgba(255,255,255,0.75)', margin: '0 auto 2rem', maxWidth: 460, lineHeight: 1.6 }}>
              Te haré una pregunta sobre el tema. Respondé con voz o texto — al instante te doy la retro.
            </p>
            <Btn $primary onClick={startSession} style={{ padding: '1rem 2rem', fontSize: '1rem' }}>
              🎙️ Empezar reto
            </Btn>
          </QuestionCard>
        </Container>
      </Page>
    );
  }

  // ═════ SIN RETO ═════
  if (stage === 'sin_reto') {
    return (
      <Page>
        <Container>
          <QuestionCard style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>{sinRetoInfo?.completado ? '✅' : '⏳'}</div>
            <h2 style={{ margin: '0 0 0.5rem' }}>
              {sinRetoInfo?.completado ? '¡Ya completaste el reto de esta semana!' : 'No hay reto activo'}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.7)' }}>
              {sinRetoInfo?.mensaje}
            </p>
            {sinRetoInfo?.proximo && (
              <p style={{ color: '#FBBF24', fontWeight: 600 }}>
                Próximo reto: {sinRetoInfo.proximo.dia} {sinRetoInfo.proximo.fecha}
              </p>
            )}
            <Btn onClick={() => navigate('/contenidos')}>← {t('ir_contenidos') || 'Volver a contenidos'}</Btn>
          </QuestionCard>
        </Container>
      </Page>
    );
  }

  // ═════ ERROR ═════
  if (stage === 'error') {
    return (
      <Page>
        <Container>
          <QuestionCard style={{ textAlign: 'center' }}>
            <h2 style={{ color: '#fca5a5' }}>⚠️ Algo salió mal</h2>
            <p style={{ color: 'rgba(255,255,255,0.7)' }}>{error}</p>
            <Btn onClick={() => navigate(-1)}>Volver</Btn>
          </QuestionCard>
        </Container>
      </Page>
    );
  }

  // ═════ RESULTADO ═════
  if (stage === 'resultado' && resultado) {
    const ok = resultado.es_correcta;
    // La puntuación del backend viene en escala 0-1 (ej. 0.85). La mostramos sobre 100.
    // Defensivo: si por algún camino llegara ya en 0-100, no la re-escalamos.
    const pct = Math.round(resultado.puntuacion <= 1 ? resultado.puntuacion * 100 : resultado.puntuacion);
    return (
      <Page>
        <Container>
          <ResultCard>
            <div style={{ fontSize: '3.5rem' }}>{ok ? '🎉' : '💪'}</div>
            <Score $ok={ok}>{pct}<span style={{ fontSize: '0.4em' }}>/100</span></Score>
            <ResultBadge $ok={ok}>{ok ? '✓ ¡Correcto!' : '× Casi'}</ResultBadge>
            {resultado.retroalimentacion && (
              <RetroBlock>
                <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: 1.2, color: '#FBBF24', textTransform: 'uppercase', marginBottom: 6 }}>
                  💬 Retroalimentación
                </div>
                <div style={{ fontSize: '0.95rem', lineHeight: 1.55, color: '#e2e8f0' }}>{resultado.retroalimentacion}</div>
              </RetroBlock>
            )}
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.6rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Btn onClick={() => navigate('/contenidos')}>← Contenidos</Btn>
              <Btn $primary onClick={() => navigate('/perfil')}>Mi perfil</Btn>
            </div>
          </ResultCard>
        </Container>
      </Page>
    );
  }

  // ═════ EN CURSO ═════
  const s = statusInfo();
  const currentText = streamingAi || pregunta || '';

  return (
    <Page>
      <Container>
        <TopBar>
          <DocTitle>
            <span>🎯</span>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '220px' }}>
              {meta?.document_title || 'Reto Semanal'}
            </span>
            <IdiomaPill>{(idioma || 'es').toUpperCase()}</IdiomaPill>
          </DocTitle>
        </TopBar>

        {micUnavailable && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(251,191,36,0.15), rgba(245,158,11,0.10))',
            border: '1px solid rgba(251,191,36,0.4)',
            borderRadius: 12,
            padding: '0.7rem 1rem',
            color: '#FBBF24',
            fontSize: '0.88rem',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span style={{ fontSize: '1.2rem' }}>🎙️🚫</span>
            <span>
              {idioma === 'en' ? 'Microphone unavailable — type your answer below.' :
               idioma === 'pt' ? 'Microfone indisponível — digite sua resposta abaixo.' :
               'Sin micrófono — escribí tu respuesta abajo.'}
            </span>
          </div>
        )}

        {noVoiceDetected && !micUnavailable && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(239,68,68,0.18), rgba(220,38,38,0.10))',
            border: '1px solid rgba(239,68,68,0.45)',
            borderRadius: 12,
            padding: '0.85rem 1rem',
            color: '#fca5a5',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}>
            <span style={{ fontSize: '1.4rem' }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 2, color: '#fecaca' }}>
                {idioma === 'en' ? 'No answer detected' : idioma === 'pt' ? 'Resposta não detectada' : 'No se reconoció tu respuesta'}
              </div>
              <div style={{ fontSize: '0.82rem', opacity: 0.9 }}>
                {idioma === 'en' ? 'Try speaking louder, closer to the mic, or type your answer below.' :
                 idioma === 'pt' ? 'Tente falar mais alto, mais perto do microfone, ou digite sua resposta abaixo.' :
                 'Hablá más fuerte y más cerca del micrófono, o escribí tu respuesta abajo.'}
              </div>
            </div>
          </div>
        )}

        <QuestionCard>
          <Pill>Reto de la semana</Pill>
          <Pregunta>
            {currentText || (
              <span style={{ opacity: 0.5 }}>
                {stage === 'conectando' ? 'Conectando con el mentor…' : 'El mentor está preparando tu pregunta…'}
              </span>
            )}
            {streamingAi && <Cursor />}
          </Pregunta>
          <Waves $active={aiState === SESSION_STATES.AI_SPEAKING || aiState === SESSION_STATES.LISTENING}>
            {Array.from({ length: 9 }).map((_, i) => (
              <Bar key={i} $i={i} $active={aiState === SESSION_STATES.AI_SPEAKING || aiState === SESSION_STATES.LISTENING} />
            ))}
          </Waves>
        </QuestionCard>

        <InputCard>
          <Textarea
            rows={2}
            placeholder="Escribe tu respuesta (o usa el micrófono). Enter para enviar"
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); } }}
            disabled={stage !== 'en_curso'}
          />
          <ActionsRow>
            <MicBtn
              $listening={aiState === SESSION_STATES.LISTENING}
              $speaking={aiState === SESSION_STATES.AI_SPEAKING}
              disabled={stage !== 'en_curso'}
              onClick={() => clientRef.current?.interrupt()}
              title="Toca para interrumpir al mentor"
            >🎤</MicBtn>
            <StatusLabel><StatusDot $color={s.dot} />{s.txt}</StatusLabel>
            <SendBtn onClick={sendText} disabled={stage !== 'en_curso' || !inputText.trim()}>Enviar</SendBtn>
          </ActionsRow>
        </InputCard>
      </Container>
    </Page>
  );
};

// ─ ErrorBoundary ─
class RetoErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) { console.error('[RetoBoundary]', error, info); }
  render() {
    if (this.state.error) {
      return (
        <Page>
          <Container>
            <QuestionCard>
              <h2 style={{ color: '#fca5a5', marginTop: 0 }}>⚠️ Error en el reto</h2>
              <pre style={{ background: 'rgba(0,0,0,0.4)', padding: '0.8rem', borderRadius: 8, overflow: 'auto', fontSize: '0.78rem', color: '#fca5a5', maxHeight: 220 }}>
                {String(this.state.error?.message || this.state.error)}
              </pre>
              <Btn onClick={() => window.location.reload()}>Recargar</Btn>
            </QuestionCard>
          </Container>
        </Page>
      );
    }
    return this.props.children;
  }
}

const RetoRealtimePageSafe = (props) => (
  <RetoErrorBoundary>
    <RetoRealtimePage {...props} />
  </RetoErrorBoundary>
);

export default RetoRealtimePageSafe;
